/**
 * ----------------------------------------------------------------------------
 * PRODUCT BUY BOX -- "Customers also buy" (conversion plan, phase 2)
 * ----------------------------------------------------------------------------
 * Two jobs, both scoped to the aside product-info renders after the trust
 * badges:
 *
 * 1. Add a row's variant to the cart. The buttons carry data-also-buy-add, not
 *    data-action="add-to-cart", because the product section's own handler
 *    serializes the FIRST /cart/add form in the section -- the main product's
 *    form -- and would add the wrong product. After a successful add this
 *    dispatches the same bubbling product:added event the theme's flow ends
 *    with (the mini-cart reads detail.quantity), so the cart UI reacts
 *    identically to both paths.
 *
 * 2. Fill the list when the merchant set no customers_also_buy metafield: the
 *    aside arrives [hidden] and empty, and Shopify's complementary-products
 *    intent supplies up to two rows. No complementary products configured
 *    (Search & Discovery app) means the aside simply stays hidden. A sold-out
 *    product gets the sold-out swap (product-swap.js) instead, whose add
 *    buttons this file's click handler also serves.
 *
 * 3. Drive the mobile sticky add-to-cart bar (phase 4): show it once the buy
 *    button has scrolled out through the top of the viewport, and keep its
 *    button and prices mirroring the selected variant. The add itself needs
 *    nothing here -- the bar sits inside the product section, so its
 *    data-action="add-to-cart" button rides the section's own handler, which
 *    serializes the main product form.
 *
 * 4. Keep the cart-aware free shipping line under the price true. product-info
 *    renders it for the first paint from cart.total_price and the selected
 *    variant; after that the price moves on variant:changed and the cart total
 *    moves on every add or drawer edit, and a stale line ("$20 away" for an
 *    item already in the cart) reads as a lie. The numbers and the four
 *    wordings ride on the element's data attributes, so nothing here depends
 *    on window.languages.
 *
 * 5. Keep the "spend more, save more" offer line true, on the same triggers
 *    and from the same kind of data attributes as the free shipping line:
 *    tier amounts and percents, the cart total and the variant price.
 *
 * 6. Play the seasonal add-to-cart ghost: on product:added from a product
 *    section, restart the peek animation on that section's ghosts.
 *
 * Loaded with defer on the product template only (layout/theme.liquid), and a
 * no-op wherever its elements are absent.
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    // /recommendations/products.json prices arrive in minor units, but be
    // tolerant of a "12.99"-style string so a platform change cannot render
    // hundred-fold prices.
    if (typeof cents === 'string' && cents.indexOf('.') !== -1) {
      cents = Math.round(parseFloat(cents) * 100);
    }
    cents = parseInt(cents, 10);
    if (isNaN(cents)) return '';

    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var value = format.indexOf('no_decimals') !== -1
      ? String(Math.round(cents / 100))
      : (cents / 100).toFixed(2);

    return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, value);
  }

  function resizedImage(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  // Built with createElement/textContent throughout: titles and URLs come from
  // a fetched document and must never reach innerHTML.
  function buildRow(product, variant) {
    var row = document.createElement('div');
    row.className = 'product-also-buy__item';

    var imageLink = document.createElement('a');
    imageLink.className = 'product-also-buy__image';
    imageLink.href = product.url;
    imageLink.tabIndex = -1;
    imageLink.setAttribute('aria-hidden', 'true');
    if (product.featured_image) {
      var image = document.createElement('img');
      image.loading = 'lazy';
      image.width = 68;
      image.height = 68;
      image.src = resizedImage(product.featured_image, 136);
      image.alt = '';
      imageLink.appendChild(image);
    }
    row.appendChild(imageLink);

    var info = document.createElement('div');
    info.className = 'product-also-buy__info';

    var name = document.createElement('a');
    name.className = 'product-also-buy__name';
    name.href = product.url;
    name.textContent = product.title;
    info.appendChild(name);

    var meta = document.createElement('div');
    meta.className = 'product-also-buy__meta';
    var price = document.createElement('span');
    price.className = 'product-also-buy__price';
    price.textContent = formatMoney(variant.price);
    meta.appendChild(price);
    info.appendChild(meta);
    row.appendChild(info);

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'product-also-buy__add';
    button.setAttribute('data-also-buy-add', '');
    button.setAttribute('data-variant-id', variant.id);
    button.textContent = (window.languages && window.languages.alsoBuyAdd) || 'Add';
    row.appendChild(button);

    return row;
  }

  function hydrateFallback(root) {
    var list = root.querySelector('[data-also-buy-list]');
    if (!list || list.querySelector('[data-also-buy-add]')) return;

    var base = root.getAttribute('data-recommendations-url') || '/recommendations/products';
    var url = base + '.json?product_id=' + encodeURIComponent(root.getAttribute('data-product-id')) +
      '&limit=4&intent=complementary';

    fetch(url, { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.json() : { products: [] }; })
      .then(function (data) {
        var added = 0;

        (data.products || []).forEach(function (product) {
          if (added >= 2 || !product.available || !product.variants) return;

          // A $0 variant is a feed glitch, not a giveaway (the $0 guard).
          var guard = !!window.zeroPriceGuard;
          var variant = null;
          for (var i = 0; i < product.variants.length; i++) {
            var candidate = product.variants[i];
            if (candidate.available && !(guard && Number(candidate.price) === 0)) { variant = candidate; break; }
          }
          if (!variant) return;

          list.appendChild(buildRow(product, variant));
          added++;
        });

        if (added > 0) root.removeAttribute('hidden');
      })
      .catch(function () { /* stays hidden */ });
  }

  function onAddClick(event) {
    var button = event.target.closest('[data-also-buy-add]');
    if (!button || button.disabled) return;

    var variantId = parseInt(button.getAttribute('data-variant-id'), 10);
    if (!variantId) return;

    button.disabled = true;
    var addUrl = ((window.routes && window.routes.cartAddUrl) || '/cart/add') + '.js';

    fetch(addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    }).then(function (response) {
      button.disabled = false;
      if (!response.ok) return;

      button.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: { button: button, variant: null, quantity: 1 }
      }));
    }).catch(function () {
      button.disabled = false;
    });
  }

  function setStickyBarState(bar, variant) {
    var button = bar.querySelector('[data-sticky-bar-button]');
    var prices = bar.querySelector('[data-sticky-bar-prices]');
    if (!button) return;

    // product-swap.js has turned the bar into "Swap to ..." for a sold-out
    // product; a variant change there is just another sold-out variant.
    if (bar.hasAttribute('data-swap-active')) return;

    var languages = window.languages || {};

    if (variant && variant.available) {
      button.disabled = false;
      button.classList.add('button--primary');
      button.classList.remove('button--disabled');
      // Re-arming data-action hands the click back to the section's own
      // add-to-cart handler.
      button.setAttribute('data-action', 'add-to-cart');
      button.textContent = languages.productFormAddToCart || 'Add to cart';
    } else {
      button.disabled = true;
      button.classList.remove('button--primary');
      button.classList.add('button--disabled');
      button.removeAttribute('data-action');
      button.textContent = variant
        ? (languages.productFormSoldOut || 'Sold out')
        : (languages.productFormUnavailable || 'Unavailable');
    }

    if (prices && variant) {
      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      prices.textContent = '';

      var price = document.createElement('span');
      price.className = 'product-sticky-bar__price' + (onSale ? ' product-sticky-bar__price--sale' : '');
      price.textContent = formatMoney(variant.price);
      prices.appendChild(price);

      if (onSale) {
        var compare = document.createElement('span');
        compare.className = 'product-sticky-bar__compare';
        compare.textContent = formatMoney(variant.compare_at_price);
        prices.appendChild(compare);
      }
    }

    // The brand deal price (shown instead while a deal is live): the variant's
    // price less the deal's percent, the discount rounded down to the cent as Shopify does.
    var dealPrices = bar.querySelector('[data-sticky-bar-deal-prices]');
    if (dealPrices && variant) {
      var percent = Number(dealPrices.getAttribute('data-deal-percent')) || 0;
      var sale = dealPrices.querySelector('[data-deal-sale]');
      var regular = dealPrices.querySelector('[data-deal-regular]');
      if (sale) sale.textContent = formatMoney(variant.price - Math.floor(variant.price * percent / 100));
      if (regular) regular.textContent = formatMoney(variant.price);
    }
  }

  function initStickyBar() {
    var bar = document.querySelector('[data-product-sticky-bar]');
    if (!bar || !('IntersectionObserver' in window)) return;

    var section = bar.closest('[data-section-type="product"]');
    var mainButton = section && section.querySelector('.product-form__add-button');
    if (!section || !mainButton) return;

    // Show only once the buy button has left through the TOP of the viewport
    // -- approaching it from above keeps the bar out of the way. The bar is
    // fixed and slides in with a transform, so toggling never shifts layout.
    var observer = new IntersectionObserver(function (entries) {
      var entry = entries[entries.length - 1];
      var passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      bar.classList.toggle('is-visible', passed);
    });
    observer.observe(mainButton);

    section.addEventListener('variant:changed', function (event) {
      setStickyBarState(bar, event.detail && event.detail.variant);
    });
  }

  // Gift cards in the cart count toward neither the free shipping threshold
  // nor any discount's minimum spend, so both nudges measure the cart without
  // them, as product-info.liquid does on first paint.
  function giftCardLines(data) {
    var cents = 0;
    var count = 0;
    (data.items || []).forEach(function (item) {
      if (item.gift_card) {
        cents += parseInt(item.final_line_price, 10) || 0;
        count += parseInt(item.quantity, 10) || 0;
      }
    });
    return { cents: cents, count: count };
  }

  function initFreeShippingNudge() {
    var nudge = document.querySelector('[data-free-shipping-nudge]');
    var text = nudge && nudge.querySelector('[data-free-shipping-text]');
    if (!nudge || !text) return;

    var threshold = parseInt(nudge.getAttribute('data-threshold-cents'), 10);
    var cart = parseInt(nudge.getAttribute('data-cart-cents'), 10) || 0;
    var cartCount = null; // unknown until the first /cart.js answer
    var variant = parseInt(nudge.getAttribute('data-variant-cents'), 10);
    if (!threshold) return;

    function render() {
      if (isNaN(variant)) return;
      var remaining = threshold - (cart + variant);

      // textContent throughout: the remaining amount is the one dynamic part,
      // and it is a number the script formatted itself.
      if (cart >= threshold) {
        text.textContent = nudge.getAttribute('data-text-cart-qualifies');
      } else if (remaining <= 0) {
        // Before the first cart fetch the server's choice between "tips it
        // over" and "qualifies on its own" still stands; afterwards the count
        // decides, matching the Liquid branch exactly.
        var inCart = cartCount === null ? cart > 0 : cartCount > 0;
        text.textContent = nudge.getAttribute(inCart ? 'data-text-item-tips' : 'data-text-item-qualifies');
      } else {
        var template = nudge.getAttribute('data-text-remaining') || '';
        var parts = template.split('[amount]');
        text.textContent = '';
        text.appendChild(document.createTextNode(parts[0]));
        var amount = document.createElement('strong');
        amount.className = 'product-form__free-shipping-amount';
        amount.textContent = formatMoney(remaining);
        text.appendChild(amount);
        text.appendChild(document.createTextNode(parts.slice(1).join('[amount]')));
      }
    }

    var section = nudge.closest('[data-section-type="product"]');
    if (section) {
      section.addEventListener('variant:changed', function (event) {
        var next = event.detail && event.detail.variant;
        // No matching variant: the price block hides itself, and the last
        // shown amount stays, same as the Klarna placement.
        if (!next) return;
        variant = parseInt(next.price, 10);
        render();
      });
    }

    var pending = false;
    function refreshCart() {
      if (pending) return;
      pending = true;
      var url = ((window.routes && window.routes.cartUrl) || '/cart') + '.js';
      fetch(url, { credentials: 'same-origin' })
        .then(function (response) { return response.ok ? response.json() : null; })
        .then(function (data) {
          pending = false;
          if (!data) return;
          var gift = giftCardLines(data);
          cart = Math.max(0, (parseInt(data.total_price, 10) || 0) - gift.cents);
          cartCount = Math.max(0, (parseInt(data.item_count, 10) || 0) - gift.count);
          render();
        })
        .catch(function () { pending = false; });
    }

    // product:added and cart:refresh bubble to the document from every add
    // path (the section's own button, the also-buy rows, the drawer add-ons).
    // cart:rerendered is dispatched on the mini cart's section without
    // bubbling after every drawer edit, so it is caught in the capture phase,
    // which reaches non-bubbling events from any ancestor.
    document.addEventListener('product:added', refreshCart);
    document.addEventListener('cart:refresh', refreshCart);
    document.addEventListener('cart:rerendered', refreshCart, true);
  }

  function initTieredOffer() {
    var offer = document.querySelector('[data-tiered-offer]');
    var text = offer && offer.querySelector('[data-tiered-offer-text]');
    var fill = offer && offer.querySelector('[data-tiered-offer-fill]');
    if (!offer || !text) return;

    function attr(name) { return parseInt(offer.getAttribute(name), 10) || 0; }
    var tier1 = attr('data-tier1-cents');
    var percent1 = attr('data-tier1-percent');
    var tier2 = attr('data-tier2-cents');
    var percent2 = attr('data-tier2-percent');
    var cart = attr('data-cart-cents');
    var variant = attr('data-variant-cents');
    if (!tier1 || !percent1) return;

    var tier2Automatic = tier2 > 0 && offer.getAttribute('data-tier2-automatic') === 'true';
    var codeLine = offer.querySelector('[data-tiered-offer-code-line]');
    var autoLine = offer.querySelector('[data-tiered-offer-auto-line]');
    var topTier = tier2 || tier1;
    var topPercent = tier2 ? percent2 : percent1;

    // The wordings arrive with [amount]-style placeholders; every value put in
    // is a number or an amount this script formatted, and the result only
    // ever reaches textContent.
    function wording(name, values) {
      var out = offer.getAttribute(name) || '';
      Object.keys(values).forEach(function (key) {
        out = out.split('[' + key + ']').join(String(values[key]));
      });
      return out;
    }

    function render() {
      var projected = cart + variant;

      if (projected >= topTier) {
        text.textContent = wording('data-text-unlocked', { percent: topPercent });
      } else if (tier2 && projected >= tier1) {
        text.textContent = wording('data-text-next-tier',
          { unlocked: percent1, amount: formatMoney(tier2 - projected), percent: percent2 });
      } else {
        text.textContent = wording('data-text-first-tier',
          { amount: formatMoney(tier1 - projected), percent: percent1 });
      }

      if (fill) fill.style.width = Math.min(100, (projected / topTier) * 100).toFixed(1) + '%';

      // An automatic second tier needs no code: the code line hides once the
      // cart reaches it (the headline already says it comes off at checkout).
      if (tier2Automatic) {
        var automaticNow = projected >= tier2;
        if (codeLine) codeLine.hidden = automaticNow;
        if (autoLine) autoLine.hidden = !automaticNow;
      }
    }

    var section = offer.closest('[data-section-type="product"]');
    if (section) {
      section.addEventListener('variant:changed', function (event) {
        var next = event.detail && event.detail.variant;
        if (!next) return;
        variant = parseInt(next.price, 10) || 0;
        render();
      });
    }

    var pending = false;
    function refreshOfferCart() {
      if (pending) return;
      pending = true;
      var url = ((window.routes && window.routes.cartUrl) || '/cart') + '.js';
      fetch(url, { credentials: 'same-origin' })
        .then(function (response) { return response.ok ? response.json() : null; })
        .then(function (data) {
          pending = false;
          if (!data) return;
          // Before order discounts, as Shopify measures the minimums, and
          // without gift cards, which no minimum counts.
          cart = Math.max(0, (parseInt(data.items_subtotal_price, 10) || 0) - giftCardLines(data).cents);
          render();
        })
        .catch(function () { pending = false; });
    }

    // The same three cart events the free shipping line listens to.
    document.addEventListener('product:added', refreshOfferCart);
    document.addEventListener('cart:refresh', refreshOfferCart);
    document.addEventListener('cart:rerendered', refreshOfferCart, true);
  }

  function initAddToCartGhost() {
    if (!document.querySelector('[data-atc-ghost]')) return;

    // theme.min.js dispatches product:added on the section element itself, the
    // also-buy rows on their button inside it; closest() covers both.
    document.addEventListener('product:added', function (event) {
      var section = event.target && event.target.closest &&
        event.target.closest('[data-section-type="product"]');
      if (!section) return;

      var ghosts = section.querySelectorAll('[data-atc-ghost]');
      for (var i = 0; i < ghosts.length; i++) {
        // Removing the class and forcing a reflow restarts the animation on a
        // second add within the same visit.
        ghosts[i].classList.remove('is-peeking');
        void ghosts[i].offsetWidth;
        ghosts[i].classList.add('is-peeking');
      }
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-product-also-buy]');
    for (var i = 0; i < roots.length; i++) hydrateFallback(roots[i]);
    // The sold-out swap's buttons (product-swap.js) ride the same handler.
    if (roots.length || document.querySelector('[data-product-swap]')) {
      document.addEventListener('click', onAddClick);
    }

    initStickyBar();
    initFreeShippingNudge();
    initTieredOffer();
    initAddToCartGhost();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
